import { useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@shared/components/ui/dialog";
import type { RunActivity } from "@shared/lib/run-activity-shared";
import { SITE_URL } from "@shared/lib/hosts";
import { useT } from "@shared/client/locale";
import type { TourVariant } from "./tour-steps";

/** La vidéo de présentation, servie par le site vitrine. */
const VIDEO = `${SITE_URL}/video/hune-agent.mp4`;
const POSTER = `${SITE_URL}/video/hune-agent-poster.jpg`;
const POLL_MS = 4_000;

const IDEAS = ["searches", "contact", "continues"] as const;

/** Pages lues par le passage en cours : un compteur réel, jamais une barre inventée. */
function usePagesRead(runId: string | null) {
  const [pages, setPages] = useState<number | null>(null);
  useEffect(() => {
    if (!runId) return;
    let stopped = false;
    async function read() {
      const activity = (await fetch(`/api/runs/${runId}/activity`).then((response) => (response.ok ? response.json() : null)).catch(() => null)) as RunActivity | null;
      if (!stopped && activity) setPages(activity.pages);
    }
    void read();
    const timer = setInterval(read, POLL_MS);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [runId]);
  return runId ? pages : null;
}

/**
 * L'accueil de la visite, à l'arrivée sur l'espace : l'analyse qui avance, la
 * vidéo, trois idées. Court : la personne veut voir son espace, pas un manuel.
 */
export function TourWelcome({ variant, runId, onStart, onSkip }: {
  variant: TourVariant;
  runId: string | null;
  onStart: () => void;
  onSkip: () => void;
}) {
  const messages = useT();
  const copy = messages.shell.tour.welcome[variant];
  const t = messages.auth.tourWelcome;
  const startRef = useRef<HTMLButtonElement>(null);
  const [playing, setPlaying] = useState(false);
  const pages = usePagesRead(variant === "running" ? runId : null);

  return (
    <Dialog open onOpenChange={(open) => !open && onSkip()}>
      <DialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-[560px]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          startRef.current?.focus();
        }}
      >
        <div className="relative aspect-[2/1] bg-muted">
          {playing ? (
            <video autoPlay className="size-full bg-black object-contain" controls playsInline poster={POSTER} src={VIDEO} />
          ) : (
            <>
              <img alt="" className="size-full object-cover" src={POSTER} />
              <button
                className="absolute bottom-4 left-5 flex cursor-pointer items-center gap-2.5 rounded-full border-0 bg-background py-1.5 pr-3.5 pl-1.5 text-[13px] font-medium text-foreground shadow-[0_4px_16px_rgba(0,0,0,0.12)] hover:bg-muted"
                onClick={() => setPlaying(true)}
                type="button"
              >
                <span className="grid size-7 place-items-center rounded-full bg-foreground text-background">
                  <Play aria-hidden="true" className="size-3 fill-current" />
                </span>
                {t.video}
              </button>
            </>
          )}
        </div>
        <div className="px-7 pt-6 pb-2 max-sm:px-5">
          <DialogTitle className="mb-1.5 text-[19px] leading-snug font-semibold tracking-[-0.01em]">{copy.title}</DialogTitle>
          <DialogDescription className="mb-4.5 text-[14px] leading-relaxed">{copy.body}</DialogDescription>
          {variant === "running" && (
            <div className="flex items-center gap-3 rounded-lg border px-3.5 py-3 text-[13.5px]">
              <span aria-hidden="true" className="size-1.5 shrink-0 rounded-full bg-success" />
              <span className="grid gap-0.5">
                {t.exploring}
                <span className="text-[12.5px] text-muted-foreground tabular-nums">{t.progress(pages)}</span>
              </span>
            </div>
          )}
          <div className="mt-5 grid grid-cols-3 gap-4 max-sm:grid-cols-1 max-sm:gap-3">
            {IDEAS.map((idea) => (
              <p className="m-0 text-[13px] leading-normal text-muted-foreground" key={idea}>
                <strong className="mb-0.5 block text-[13.5px] font-medium text-foreground">{t.ideas[idea].title}</strong>
                {t.ideas[idea].body}
              </p>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-between gap-4 px-7 pt-4 pb-5.5 text-[12.5px] text-muted-foreground max-sm:flex-col-reverse max-sm:items-stretch max-sm:px-5">
          <span className="max-sm:text-center">{copy.note}</span>
          <Button className="h-10 px-4.5 max-md:h-11" onClick={onStart} ref={startRef}>
            {t.start}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
